<template>
  <b-card class="mb-3" body-class="p-3">
    <b-form @submit.prevent="search">
      <b-input-group size="sm">
        <b-form-input v-model.trim="query" :placeholder="$t('typeUserNameOrIdAndHitEnter')" autofocus/>
        <b-input-group-append>
          <b-button type="submit" variant="outline-primary" :disabled="!canSearch || searching">
            <b-spinner v-if="searching" small/>
            <span v-else>{{ $t('search') }}</span>
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <b-form-checkbox v-model="ownSchoolOnly" size="sm" class="mt-1">
        {{ $t('ownSchoolOnly') }}
      </b-form-checkbox>
    </b-form>

    <b-alert variant="danger" :show="searchError" class="mt-2 mb-0">{{ $t('searchError') }}</b-alert>

    <div v-if="hasSearched && !searching" class="mt-2">
      <div v-if="results.length === 0" class="text-info">{{ $t('noResults') }}</div>
      <div v-else class="assign-results border rounded">
        <b-form-checkbox v-for="user in results" :key="user.userId"
                         :checked="isSelected(user)" class="px-2 py-1"
                         @change="toggle(user, $event)">
          {{ user.fullName }} <span class="text-muted">({{ user.username }})</span>
          <b-badge v-if="existingUserIds.includes(user.userId)" variant="light" class="ml-1">{{ $t('hasUsage') }}</b-badge>
        </b-form-checkbox>
      </div>
    </div>

    <div v-if="selected.length" class="mt-3">
      <strong class="d-block mb-1">{{ $t('selectedUsers') }} ({{ selected.length }})</strong>
      <b-badge v-for="user in selected" :key="user.userId" pill
               :variant="failedUserIds.includes(user.userId) ? 'danger' : 'secondary'" class="mr-1 mb-1">
        {{ user.fullName || user.username }}
        <a href="#" class="text-white ml-1" :aria-label="$t('remove')" @click.prevent="toggle(user, false)">&times;</a>
      </b-badge>
    </div>

    <b-alert variant="danger" :show="failedUserIds.length > 0" class="mt-2 mb-0">
      {{ $t('assignFailed', { count: failedUserIds.length }) }}
    </b-alert>

    <div class="mt-3 d-flex justify-content-end">
      <b-button size="sm" variant="secondary" class="mr-2" :disabled="saving" @click="$emit('close')">{{ $t('cancel') }}</b-button>
      <b-button size="sm" variant="primary" :disabled="!selected.length || saving" @click="assign">
        <b-spinner v-if="saving" small class="mr-1"/>
        {{ $t('setActive', { count: selected.length }) }}
      </b-button>
    </div>
  </b-card>
</template>

<script>
  import UserService from '../../services/UserService'
  import LicenseService from '../../services/LicenseService'

  export default {
    name: 'AssignUsers',
    props: ['license', 'existingUserIds'],

    data () {
      return {
        query: '',
        ownSchoolOnly: true,
        results: [],
        selected: [],
        searching: false,
        hasSearched: false,
        searchError: false,
        saving: false,
        failedUserIds: []
      }
    },

    computed: {
      canSearch () {
        return this.query.length >= 3
      }
    },

    methods: {
      async search () {
        if (!this.canSearch) return
        this.searching = true
        this.searchError = false
        try {
          const organizationNumber = this.ownSchoolOnly ? this.license.ownerOrganizationNumber : undefined
          const data = await UserService.searchUser(this.query, organizationNumber)
          this.results = data.response || []
        } catch (e) {
          this.results = []
          this.searchError = true
        } finally {
          this.searching = false
          this.hasSearched = true
        }
      },
      isSelected (user) {
        return this.selected.some(u => u.userId === user.userId)
      },
      toggle (user, checked) {
        if (checked && !this.isSelected(user)) {
          this.selected.push(user)
        } else if (!checked) {
          this.selected = this.selected.filter(u => u.userId !== user.userId)
          this.failedUserIds = this.failedUserIds.filter(id => id !== user.userId)
        }
      },
      // The force endpoint only updates existing usage rows, so register access first to create one.
      // Access may be denied (e.g. no free seats) — the force call decides whether this succeeded.
      async activate (user, licenseId) {
        if (!this.existingUserIds.includes(user.userId)) {
          try {
            await LicenseService.registerProductAccess(this.license.ean, user.userId, this.license.ownerOrganizationNumber)
          } catch (e) {
            // Ignored, see above
          }
        }
        return LicenseService.setForcedOnLicenseUsage(user.userId, licenseId, 'ACTIVE')
      },
      async assign () {
        const licenseId = this.license.licenseId
        this.saving = true
        this.failedUserIds = []
        const outcomes = await Promise.allSettled(this.selected.map(user => this.activate(user, licenseId)))
        this.failedUserIds = this.selected
          .filter((user, i) => outcomes[i].status === 'rejected')
          .map(user => user.userId)
        // Keep only the failed users selected so they can be retried
        this.selected = this.selected.filter(user => this.failedUserIds.includes(user.userId))
        this.saving = false
        this.$emit('assigned')
        if (!this.failedUserIds.length) this.$emit('close')
      }
    },

    i18n: {
      messages: {
        en: {
          'ownSchoolOnly': 'Only users at this school',
          'searchError': 'User search failed',
          'hasUsage': 'Has usage',
          'selectedUsers': 'Selected users',
          'remove': 'Remove',
          'setActive': 'Set active ({count})',
          'assignFailed': 'Could not set the license active for {count} user(s). They are still selected so you can retry.'
        },
        nb: {
          'ownSchoolOnly': 'Kun brukere ved denne skolen',
          'searchError': 'Brukersøket feilet',
          'hasUsage': 'Har bruk',
          'selectedUsers': 'Valgte brukere',
          'remove': 'Fjern',
          'setActive': 'Sett aktiv ({count})',
          'assignFailed': 'Kunne ikke sette lisensen aktiv for {count} bruker(e). De er fortsatt valgt så du kan prøve igjen.'
        }
      }
    }
  }
</script>

<style scoped>
  .assign-results {
    max-height: 240px;
    overflow-y: auto;
  }
</style>
